import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Link2, Scissors, ArrowLeftRight, Layers } from 'lucide-react';
import { useChemistryStore } from '../../../stores/chemistryStore';
import { useNavigationStore } from '../../../stores/navigationStore';

interface DesignTabProps {
  smiles: string;
  moleculeId?: number;
  fileId?: number;
}

interface WorkflowCard {
  id: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  route: string;
  paramName: string;
  available: boolean;
}

const DesignTab: React.FC<DesignTabProps> = ({ smiles, moleculeId, fileId }) => {
  const navigate = useNavigate();
  const currentFile = useChemistryStore((s) => s.currentFile);
  const navProjectName = useNavigationStore((s) => s.projectName);
  const effectiveFileId = fileId ?? (currentFile?.id ? Number(currentFile.id) : undefined);

  const workflows: WorkflowCard[] = [
    {
      id: 'protac',
      title: 'PROTAC Designer',
      description: 'Design PROTACs using this compound as a warhead',
      icon: <Link2 size={20} />,
      route: '/workbench/protac',
      paramName: 'warhead',
      available: false,
    },
    {
      id: 'fragment-grow',
      title: 'Fragment Growing',
      description: 'Grow fragments from this scaffold using R-group enumeration',
      icon: <Scissors size={20} />,
      route: '/workbench/fragment-grow',
      paramName: 'seed',
      available: false,
    },
    {
      id: 'retrosynthesis',
      title: 'Retrosynthesis',
      description: 'Plan synthetic routes to this compound',
      icon: <ArrowLeftRight size={20} />,
      route: '/workbench/retrosynthesis',
      paramName: 'target',
      available: false,
    },
    {
      id: 'scaffold-hop',
      title: 'Scaffold Hopping',
      description: 'Find bioisosteric replacements for this scaffold',
      icon: <Layers size={20} />,
      route: '/workbench/scaffold-hop',
      paramName: 'query',
      available: false,
    },
  ];

  const handleClick = (wf: WorkflowCard) => {
    if (!wf.available) return;
    const params = new URLSearchParams();
    params.set(wf.paramName, smiles);
    if (moleculeId) params.set('molecule_id', String(moleculeId));
    if (effectiveFileId) params.set('file_id', String(effectiveFileId));
    if (currentFile?.projectId) params.set('project_id', String(currentFile.projectId));
    if (navProjectName) params.set('project_name', navProjectName);
    if (currentFile?.fileName) params.set('file_name', currentFile.fileName);
    const path = `${wf.route}?${params.toString()}`;
    // Set contextual back link before navigating
    const currentUrl = window.location.pathname + window.location.search;
    const backLabel = [navProjectName, currentFile?.fileName].filter(Boolean).join(' > ') || 'Editor';
    useNavigationStore.getState().setPreviousNavigation(currentUrl, backLabel);
    if (window.opener) { window.opener.location.href = window.location.origin + path; }
    else { navigate(path); }
  };

  return (
    <div className="space-y-3">
      <div className="text-xs" style={{ color: 'var(--text-muted)' }}>
        Launch workflows pre-populated with this compound
      </div>

      <div className="grid grid-cols-1 gap-2">
        {workflows.map((wf) => (
          <button
            key={wf.id}
            onClick={() => handleClick(wf)}
            disabled={!wf.available}
            className="text-left rounded-lg border p-3 transition-all group"
            style={{
              borderColor: wf.available ? 'var(--card-border)' : 'var(--card-border)',
              backgroundColor: wf.available ? 'var(--card-bg)' : 'var(--content-bg)',
              opacity: wf.available ? 1 : 0.6,
              cursor: wf.available ? 'pointer' : 'default',
            }}
            onMouseEnter={(e) => {
              if (wf.available) {
                e.currentTarget.style.borderColor = '#0d9488';
                e.currentTarget.style.backgroundColor = '#f0fdfa';
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = wf.available ? 'var(--card-border)' : 'var(--card-border)';
              e.currentTarget.style.backgroundColor = wf.available ? 'var(--card-bg)' : 'var(--content-bg)';
            }}
          >
            <div className="flex items-start gap-3">
              <div
                className="p-2 rounded-lg flex-shrink-0"
                style={{
                  backgroundColor: wf.available ? '#f0fdfa' : 'var(--hover-bg)',
                  color: wf.available ? '#0d9488' : 'var(--text-muted)',
                }}
              >
                {wf.icon}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span
                    className="text-sm font-medium"
                    style={{ color: wf.available ? 'var(--text-primary)' : 'var(--text-muted)' }}
                  >
                    {wf.title}
                  </span>
                  <span
                    className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium"
                    style={{
                      backgroundColor: wf.available ? '#10b98118' : 'var(--hover-bg)',
                      color: wf.available ? '#10b981' : 'var(--text-muted)',
                      border: `1px solid ${wf.available ? '#10b98130' : 'var(--card-border)'}`,
                    }}
                  >
                    {wf.available ? 'Available' : 'Coming Soon'}
                  </span>
                </div>
                <p className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>
                  {wf.description}
                </p>
              </div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
};

export default DesignTab;
